import { LIMIT_KINDS, LIMIT_TITLES, MODULES, PLAN_MODULES, type Plan } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast, unboundErrors } from '../../ui/feedback';
import { Checkbox, Field, Input, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { moduleTitle } from '../admin/LicensePage';
import { limitsText, parseLimit, plansQuery, priceText } from './shared';

/** Поля формы со своей подписью ошибки */
const PLAN_FIELDS = [
  'code',
  'name',
  'description',
  'priceMonth',
  'priceYear',
  'currency',
  'sortOrder',
  ...LIMIT_KINDS.map((k) => `limits.${k}`),
];

type Form = {
  code: string;
  name: string;
  description: string;
  modules: string[];
  limits: Record<(typeof LIMIT_KINDS)[number], string>;
  priceMonth: string;
  priceYear: string;
  currency: string;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: string;
};

function PlanDialog({ plan, onClose }: { plan: Plan | 'new'; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = plan === 'new';
  const [f, setF] = useState<Form>({
    code: isNew ? '' : plan.code,
    name: isNew ? '' : plan.name,
    description: isNew ? '' : (plan.description ?? ''),
    modules: isNew ? [...PLAN_MODULES] : plan.modules,
    limits: Object.fromEntries(
      LIMIT_KINDS.map((k) => [k, isNew || plan.limits[k] == null ? '' : String(plan.limits[k])]),
    ) as Form['limits'],
    priceMonth: isNew || plan.priceMonth == null ? '' : String(plan.priceMonth),
    priceYear: isNew || plan.priceYear == null ? '' : String(plan.priceYear),
    currency: isNew ? 'KZT' : plan.currency,
    isActive: isNew ? true : plan.isActive,
    isPublic: isNew ? true : plan.isPublic,
    sortOrder: isNew ? '0' : String(plan.sortOrder),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: f.code,
        name: f.name,
        description: f.description.trim() || null,
        modules: f.modules,
        limits: Object.fromEntries(LIMIT_KINDS.map((k) => [k, parseLimit(f.limits[k])])),
        priceMonth: f.priceMonth.trim() ? Number(f.priceMonth) : null,
        priceYear: f.priceYear.trim() ? Number(f.priceYear) : null,
        currency: f.currency.toUpperCase(),
        isActive: f.isActive,
        isPublic: f.isPublic,
        sortOrder: Number(f.sortOrder) || 0,
      };
      return isNew
        ? api('/platform/plans', { body })
        : api(`/platform/plans/${plan.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast(isNew ? 'Тариф добавлен' : 'Тариф сохранён');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    save.mutate();
  }

  const toggleModule = (code: string, on: boolean) =>
    setF((x) => ({
      ...x,
      modules: on ? [...new Set([...x.modules, code])] : x.modules.filter((m) => m !== code),
    }));

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новый тариф' : `Тариф «${plan.name}»`}
      description={
        isNew
          ? undefined
          : `${plan.tenantsCount} компаний на этом тарифе — изменения разделов и лимитов применятся к ним сразу.`
      }
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="plan-form" loading={save.isPending}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <form id="plan-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Название" htmlFor="pl-name" error={errors.name}>
          <Input
            id="pl-name"
            value={f.name}
            onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))}
            invalid={!!errors.name}
          />
        </Field>
        <Field
          label="Код"
          htmlFor="pl-code"
          hint="Латиница, цифры и дефис; виден только владельцу портала"
          error={errors.code}
        >
          <Input
            id="pl-code"
            value={f.code}
            onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toLowerCase() }))}
            invalid={!!errors.code}
            disabled={!isNew}
          />
        </Field>
        <Field
          label="Описание"
          htmlFor="pl-desc"
          hint="Одной фразой: для кого тариф"
          className="sm:col-span-2"
          error={errors.description}
        >
          <Textarea
            id="pl-desc"
            value={f.description}
            onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))}
            rows={2}
          />
        </Field>
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-[13px] font-medium text-ink">Разделы сверх основы портала</legend>
          <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2 md:grid-cols-3">
            {PLAN_MODULES.map((code) => (
              <Checkbox
                key={code}
                label={moduleTitle(code)}
                description={
                  MODULES.find((m) => m.code === code)?.tzSection
                    ? `раздел ${MODULES.find((m) => m.code === code)?.tzSection} ТЗ`
                    : undefined
                }
                checked={f.modules.includes(code)}
                onChange={(e) => toggleModule(code, e.target.checked)}
              />
            ))}
          </div>
          <p className="mt-2 text-[12px] text-ink-2">
            Основа портала («Ждёт меня», обзор, портфель, администрирование, «Что нового») входит в любой
            тариф.
          </p>
        </fieldset>
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-[13px] font-medium text-ink">Лимиты</legend>
          <div className="grid gap-3 sm:grid-cols-5">
            {LIMIT_KINDS.map((k) => (
              <Field key={k} label={LIMIT_TITLES[k]} htmlFor={`pl-lim-${k}`} error={errors[`limits.${k}`]}>
                <Input
                  id={`pl-lim-${k}`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="∞"
                  value={f.limits[k]}
                  onChange={(e) => setF((x) => ({ ...x, limits: { ...x.limits, [k]: e.target.value } }))}
                />
              </Field>
            ))}
          </div>
          <p className="mt-1.5 text-[12px] text-ink-2">Пусто — без ограничения.</p>
        </fieldset>
        <Field
          label="Цена в месяц"
          htmlFor="pl-pm"
          hint="Без НДС; пусто — по договорённости"
          error={errors.priceMonth}
        >
          <Input
            id="pl-pm"
            type="number"
            inputMode="decimal"
            min={0}
            value={f.priceMonth}
            onChange={(e) => setF((x) => ({ ...x, priceMonth: e.target.value }))}
          />
        </Field>
        <Field
          label="Цена в год"
          htmlFor="pl-py"
          hint="Без НДС; пусто — по договорённости"
          error={errors.priceYear}
        >
          <Input
            id="pl-py"
            type="number"
            inputMode="decimal"
            min={0}
            value={f.priceYear}
            onChange={(e) => setF((x) => ({ ...x, priceYear: e.target.value }))}
          />
        </Field>
        <Field label="Валюта" htmlFor="pl-cur" error={errors.currency}>
          <Input
            id="pl-cur"
            value={f.currency}
            maxLength={3}
            onChange={(e) => setF((x) => ({ ...x, currency: e.target.value }))}
          />
        </Field>
        <Field
          label="Порядок"
          htmlFor="pl-sort"
          hint="Чем меньше, тем выше в списке"
          error={errors.sortOrder}
        >
          <Input
            id="pl-sort"
            type="number"
            inputMode="numeric"
            value={f.sortOrder}
            onChange={(e) => setF((x) => ({ ...x, sortOrder: e.target.value }))}
          />
        </Field>
        <Checkbox
          label="В продаже"
          description="Снятый с продажи тариф нельзя выбрать для новой лицензии; действующие лицензии продолжают работать"
          checked={f.isActive}
          onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))}
        />
        <Checkbox
          label="Показывать на главной странице"
          description="В публичном списке тарифов и в форме заявки на доступ"
          checked={f.isPublic}
          onChange={(e) => setF((x) => ({ ...x, isPublic: e.target.checked }))}
        />
        <ErrorBox
          className="sm:col-span-2"
          error={Object.keys(errors).length ? unboundErrors(errors, PLAN_FIELDS) : save.error}
        />
      </form>
    </Dialog>
  );
}

/** Тарифы портала (ЛЦ-01): разделы, лимиты, цены; редактирует владелец портала */
export function PlansPage() {
  const q = useQuery(plansQuery);
  const [editing, setEditing] = useState<Plan | 'new' | null>(null);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <>
      <Panel
        title="Тарифы"
        meta="ЛЦ-01"
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-4" />}
            onClick={() => setEditing('new')}
          >
            Новый тариф
          </Button>
        }
        bodyClassName="p-0"
      >
        {!q.data.length ? (
          <div className="p-5">
            <EmptyState title="Тарифов нет">
              Добавьте первый тариф — без него нельзя выпустить лицензию.
            </EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {q.data.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setEditing(p)}
                  className="flex w-full min-h-[44px] flex-col gap-1 px-5 py-3 text-left hover:bg-surface-2 sm:flex-row sm:items-start sm:gap-4"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">{p.name}</span>
                      <span className="text-[12px] text-ink-2">{p.code}</span>
                      {!p.isActive ? <Badge>снят с продажи</Badge> : null}
                      {!p.isPublic ? <Badge>не на главной</Badge> : null}
                    </div>
                    {p.description ? <div className="text-[13px] text-ink-2">{p.description}</div> : null}
                    <div className="mt-1 text-[13px] text-ink-2">
                      {p.modules.length === PLAN_MODULES.length
                        ? 'Все разделы'
                        : `${p.modules.length} из ${PLAN_MODULES.length} разделов`}{' '}
                      · {limitsText(p.limits)}
                    </div>
                  </div>
                  <div className="shrink-0 text-[13px] text-ink-2 sm:text-right">
                    <div className="num">{priceText(p.priceMonth, p.currency, 'мес')}</div>
                    <div className="num">{priceText(p.priceYear, p.currency, 'год')}</div>
                    <div className="mt-1">{p.tenantsCount} компаний</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {editing ? <PlanDialog plan={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}
